import { useState, useCallback } from "react";
import { Box, IconButton, Typography, Fade } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import PropTypes from "prop-types";

const ImageCarousel = ({
  images,
  onImageClick,
  t,
  convertUTCToLocal,
  compact = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setCurrentImageIndex((prev) =>
          prev === 0 ? images.length - 1 : prev - 1
        );
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setCurrentImageIndex((prev) =>
          prev === images.length - 1 ? 0 : prev + 1
        );
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onImageClick(images[currentImageIndex].url);
      }
    },
    [images, currentImageIndex, onImageClick]
  );

  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleImageClick = () => {
    onImageClick(images[currentImageIndex].url);
  };

  if (images.length === 0) return null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: compact ? 200 : { xs: 300, sm: 400, md: 500 },
        borderRadius: compact ? 2 : 3,
        overflow: "hidden",
        bgcolor: "background.paper",
        border: compact ? "none" : "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          borderColor: compact ? "transparent" : "primary.main",
          boxShadow: compact ? 2 : 6,
        },
        "&:focus": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleImageClick}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label={t(
        "documentDetail.imageCarouselAria",
        "Image carousel. Use arrow keys to navigate, Enter to view full size"
      )}
    >
      {/* Main Image */}
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <img
          src={images[currentImageIndex].url}
          alt={
            images[currentImageIndex].display_name ||
            t("documentDetail.imageAlt")
          }
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#f5f5f5",
          }}
          loading="lazy"
        />

        {/* Overlay with image info */}
        <Fade in={isHovered}>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
              color: "white",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {images[currentImageIndex].display_name ||
                  t("documentDetail.imageLabel")}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {convertUTCToLocal(images[currentImageIndex].created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ZoomInIcon sx={{ fontSize: 20 }} />
              <Typography variant="caption">
                {t("documentDetail.clickToEnlarge", "Click to enlarge")}
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Navigation arrows for multiple images */}
        {images.length > 1 && (
          <>
            <Fade in={isHovered}>
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: "absolute",
                  left: compact ? 8 : 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0,0,0,0.7)",
                  color: "white",
                  width: compact ? 36 : 48,
                  height: compact ? 36 : 48,
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.9)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: compact ? 20 : 24 }} />
              </IconButton>
            </Fade>

            <Fade in={isHovered}>
              <IconButton
                onClick={handleNext}
                sx={{
                  position: "absolute",
                  right: compact ? 8 : 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "rgba(0,0,0,0.7)",
                  color: "white",
                  width: compact ? 36 : 48,
                  height: compact ? 36 : 48,
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.9)",
                    transform: "translateY(-50%) scale(1.1)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ChevronRightIcon sx={{ fontSize: compact ? 20 : 24 }} />
              </IconButton>
            </Fade>
          </>
        )}

        {/* Image counter */}
        {images.length > 1 && (
          <Box
            sx={{
              position: "absolute",
              top: compact ? 8 : 16,
              right: compact ? 8 : 16,
              bgcolor: "rgba(0,0,0,0.7)",
              color: "white",
              px: compact ? 1.5 : 2,
              py: compact ? 0.5 : 1,
              borderRadius: compact ? 1.5 : 2,
              backdropFilter: "blur(4px)",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: compact ? "0.65rem" : "0.75rem",
              }}
            >
              {currentImageIndex + 1} / {images.length}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Thumbnail navigation for multiple images */}
      {images.length > 1 && !compact && (
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
            bgcolor: "rgba(0,0,0,0.7)",
            p: 1,
            borderRadius: 2,
            backdropFilter: "blur(4px)",
          }}
        >
          {images.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentImageIndex(index);
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor:
                  index === currentImageIndex
                    ? "white"
                    : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "white",
                  transform: "scale(1.2)",
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.array.isRequired,
  onImageClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  convertUTCToLocal: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

export default ImageCarousel;
